/* eslint-disable import/no-cycle */
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import WsSectionContainer from '../WsSectionContainer';
import { PRODUCT_CATEGORY_LIST } from '../../../../../config/productCategories';
import {
  doesResourcesHaveProducts,
  getSelectedResourceBaseNavigationUrl,
  getWebstoreHeadingSize
} from '../../../../../helpers/webstoreHelper';
import WsCategoryGridDisplay from '../categoryGrid/WsCategoryGridDisplay';
import WsSectionViewAllButtons from '../WsSectionViewAllButtons';

const WsCategoryList = ({
  config,
  colorSchemes,
  themeConfig,
  baseStoreUrl,
  onNavigate,
  ecomStoreTheme,
  mediaQueries,
  productCategories,
  blocks,
  resourceNavigation,
  pageWidth,
  selectedResources,
  ...rest
}) => {
  const { state } = useContext(StoreUserContext);
  const { isTabletOrSmaller } = mediaQueries;
  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const isSubcategoryList = config.isSubcategoryList === 'true';
  const isSubcategoriesUnfiltered = config.isSubcategoriesUnfiltered === 'true';
  const {
    ecomCategory,
    ecomSubcategory,
    ecomStoreDesign,
    ecomStoreCollection
  } = selectedResources;
  const baseResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    isSubcategoryList ? 'sub-categories' : 'categories'
  )}`;

  const products = ecomStoreTheme.ecomStore.ecomStoreProducts || [];

  const displayCategories = useMemo(() => {
    if (blocks.length > 0) {
      if (isSubcategoryList) {
        return blocks
          .filter((b) =>
            doesResourcesHaveProducts(ecomStoreTheme, {
              pkEcomCategory: ecomCategory?.value,
              pkEcomVendorDesign: ecomStoreDesign?.pkEcomVendorDesign,
              pkEcomStoreCollection: ecomStoreCollection?.pkEcomStoreCollection,
              ecomSubcategory: b.config?.ecomSubcategory
            })
          )
          .map((b) => ({
            name: b.config?.ecomCategory?.toLowerCase() ?? '',
            value: b.config?.ecomCategory?.toLowerCase() ?? ''
          }));
      }

      return blocks
        .filter((b) =>
          doesResourcesHaveProducts(ecomStoreTheme, {
            pkEcomCategory: b.config?.ecomCategory,
            pkEcomVendorDesign: ecomStoreDesign?.pkEcomVendorDesign,
            pkEcomStoreCollection: ecomStoreCollection?.pkEcomStoreCollection,
            ecomSubcategory: ecomSubcategory?.value
          })
        )
        .map((b) =>
          PRODUCT_CATEGORY_LIST.find(
            (pc) => pc.value.toString() === b.config?.ecomCategory
          )
        )
        .filter(Boolean);
    }

    if (isSubcategoryList) {
      return resourceNavigation.some(
        (nav) => nav.type === 'sub-categories' && !nav.id
      )
        ? []
        : productCategories.subcategories
            .filter((sc) =>
              doesResourcesHaveProducts(ecomStoreTheme, {
                pkEcomCategory: ecomCategory?.value,
                pkEcomVendorDesign: ecomStoreDesign?.pkEcomVendorDesign,
                pkEcomStoreCollection:
                  ecomStoreCollection?.pkEcomStoreCollection,
                ecomSubcategory: sc
              })
            )
            .map((c) => ({ name: c, value: c }));
    }
    return ecomCategory
      ? []
      : productCategories.categories
          .map((c) => PRODUCT_CATEGORY_LIST.find((pc) => pc.value === c))
          .filter(
            (c) =>
              c &&
              doesResourcesHaveProducts(ecomStoreTheme, {
                pkEcomCategory: c.value,
                pkEcomVendorDesign: ecomStoreDesign?.pkEcomVendorDesign,
                pkEcomStoreCollection:
                  ecomStoreCollection?.pkEcomStoreCollection,
                ecomSubcategory: ecomSubcategory?.value
              })
          );
  }, [
    blocks,
    isSubcategoryList,
    isSubcategoriesUnfiltered,
    products,
    ecomCategory?.value,
    ecomStoreDesign?.pkEcomVendorDesign,
    productCategories,
    PRODUCT_CATEGORY_LIST
  ]);

  const maxDisplayed = config.maximumDisplayed * 1 ?? 25;

  const isHidden = isSubcategoryList
    ? !!ecomSubcategory || displayCategories.length === 0
    : !!ecomSubcategory ||
      (displayCategories.length === 0 && config.hideEmptyList === 'true');

  return (
    !isHidden && (
      <WsSectionContainer
        colorScheme={colorScheme}
        config={{
          ...config,
          fullWidth: 'true'
        }}
        mediaQueries={mediaQueries}
        themeConfig={themeConfig}
        {...rest}
      >
        <AppStack
          style={{
            flex: 1,
            justifyContent: 'space-between',
            margin: 'auto',
            width: '100%',
            alignItems: 'center',
            gap: 16
          }}
        >
          {(config.headingText || config.descriptionText) && (
            <AppStack
              style={{
                width: '100%',
                maxWidth: config.fullWidth === 'true' ? 'unset' : pageWidth,
                gap: 5
              }}
            >
              {config.headingText && (
                <AppText
                  style={{
                    fontWeight: 600,
                    fontSize: getWebstoreHeadingSize(config.headingSize)
                  }}
                >
                  {config.headingText}
                </AppText>
              )}
              {config.descriptionText && (
                <AppText style={{ fontSize: 16 }}>
                  {config.descriptionText}
                </AppText>
              )}
            </AppStack>
          )}

          <AppStack
            style={{
              flex: 1,
              justifyContent: 'space-between',
              margin: 'auto',
              width: '100%',
              maxWidth:
                config.categoriesFullWidth === 'true' ? 'unset' : pageWidth,
              gap: 0
            }}
          >
            <WsCategoryGridDisplay
              baseStoreUrl={baseResourceUrl}
              categoryValues={displayCategories
                .slice(0, maxDisplayed)
                .map((c) => c.value)}
              colorSchemes={colorSchemes}
              config={config}
              ecomStoreTheme={ecomStoreTheme}
              isSubcategoryList={isSubcategoryList}
              mediaQueries={mediaQueries}
              noSort={blocks.length > 0}
              onNavigate={onNavigate}
            />

            {config.enableViewAll === 'true' &&
              displayCategories.length > maxDisplayed && (
                <WsSectionViewAllButtons
                  buttonStyleType={config.viewAllButtonStyle}
                  colorScheme={colorScheme}
                  onNavigate={onNavigate}
                  url={`${baseResourceUrl}/${
                    isSubcategoryList ? 'sub-categories' : 'categories'
                  }`}
                />
              )}
          </AppStack>
        </AppStack>
      </WsSectionContainer>
    )
  );
};

WsCategoryList.propTypes = {
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pageWidth: PropTypes.number,
  productCategories: PropTypes.object,
  resourceNavigation: PropTypes.array,
  selectedResources: PropTypes.object,
  themeConfig: PropTypes.object
};

export default WsCategoryList;
